// TODO: Add user consent note to privacy policy confirming writing patterns are used only to
// personalise suggestions for that user and are never shared or used to train models.

const STYLE_KEY    = 'writingStyleProfile';
const PATTERNS_KEY = 'writingLearnedPatterns';

export const TONE_OPTIONS = [
  'Direct', 'Warm', 'Formal', 'Casual', 'Friendly',
  'Concise', 'Detailed', 'Professional', 'Conversational',
];

export const GREETING_OPTIONS = [
  { value: 'hi',    label: 'Hi [name]' },
  { value: 'hello', label: 'Hello [name]' },
  { value: 'dear',  label: 'Dear [name]' },
  { value: 'hey',   label: 'Hey [name]' },
  { value: 'none',  label: 'No greeting' },
];

export const SENTENCE_STYLE_OPTIONS = [
  { value: 'short',    label: 'Short and punchy' },
  { value: 'balanced', label: 'Balanced' },
  { value: 'long',     label: 'Long and detailed' },
];

export const DEFAULT_PROFILE = {
  toneKeywords:    [],
  wordsToAvoid:    [],
  greetingStyle:   'hi',
  signOff:         '',
  sentenceStyle:   'balanced',
};

// ── Storage helpers ───────────────────────────────────────────────────────────
// Block 226: the style PROFILE no longer lives in localStorage. It is User.writingStyle,
// a server column, and this module now only parses it. It was previously written to
// BOTH the column and localStorage but read back only from localStorage, so a profile
// edited on one device was invisible on every other — and saving on the second device
// overwrote the good server copy with defaults.
//
// LEARNED PATTERNS (patternsKey below) are a different thing and stay in localStorage:
// they are derived from analysing text the user typed on this device, not settings the
// user chose, and nothing server-side reads them.
function patternsKey(userId) { return userId ? `${PATTERNS_KEY}_${userId}` : PATTERNS_KEY; }

/**
 * Parse User.writingStyle into a profile, merged over the defaults.
 *
 * Defensive because the column is a nullable string that does not always hold JSON —
 * some rows carry a plain prose description ("professional and warm"), which is not a
 * profile and must read as "unset" rather than throwing. Mirrors
 * `Aidey Backend/lib/writingStyle.js`.
 *
 * @param {object|null} user  the currentUser object (or anything with .writingStyle)
 */
export function loadStyleProfile(user) {
  const raw = user && typeof user === 'object' ? user.writingStyle : null;
  if (!raw) return { ...DEFAULT_PROFILE };
  if (typeof raw === 'object' && !Array.isArray(raw)) return { ...DEFAULT_PROFILE, ...raw };
  if (typeof raw !== 'string') return { ...DEFAULT_PROFILE };
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ? { ...DEFAULT_PROFILE, ...parsed }
      : { ...DEFAULT_PROFILE };
  } catch {
    return { ...DEFAULT_PROFILE };
  }
}

// Block 226: one migration-time cleanup. The old localStorage copy is now unread, and
// leaving it behind would let a future reader resurrect stale settings. Removing it is
// safe because the server column is authoritative and was written on every save.
export function clearLegacyStyleProfile(userId) {
  try {
    localStorage.removeItem(userId ? `${STYLE_KEY}_${userId}` : STYLE_KEY);
  } catch { /* private mode / storage disabled, nothing to clean */ }
}

export function loadLearnedPatterns(userId) {
  try {
    const raw = localStorage.getItem(patternsKey(userId));
    return raw ? JSON.parse(raw) : { sampleCount: 0, avgSentenceLength: 0, vocabularyComplexity: 0, commonOpenings: [], commonClosings: [] };
  } catch {
    return { sampleCount: 0, avgSentenceLength: 0, vocabularyComplexity: 0, commonOpenings: [], commonClosings: [] };
  }
}

function saveLearnedPatterns(userId, patterns) {
  localStorage.setItem(patternsKey(userId), JSON.stringify(patterns));
}

// ── Background writing analysis (silent — never shown to user directly) ───────
export function analyseWritingSample(text, userId) {
  if (!text || text.length < 50) return;

  const sentences = text.split(/[.!?]+/).map(s => s.trim()).filter(s => s.length > 3);
  if (!sentences.length) return;

  const wordCounts   = sentences.map(s => s.split(/\s+/).length);
  const avgWords     = Math.round(wordCounts.reduce((a, b) => a + b, 0) / wordCounts.length);
  const words        = text.replace(/[^a-z\s]/gi, ' ').split(/\s+/).filter(Boolean);
  const avgWordLen   = words.length
    ? Math.round(words.reduce((a, w) => a + w.length, 0) / words.length)
    : 5;

  const opening = sentences[0]?.slice(0, 70) || '';
  const closing = sentences[sentences.length - 1]?.slice(0, 70) || '';

  const existing = loadLearnedPatterns(userId);
  const n        = existing.sampleCount || 0;

  const updated = {
    avgSentenceLength:   Math.round(((existing.avgSentenceLength || 0) * n + avgWords) / (n + 1)),
    vocabularyComplexity: Math.round(((existing.vocabularyComplexity || 0) * n + avgWordLen) / (n + 1)),
    commonOpenings: [...new Set([...(existing.commonOpenings || []), opening])].slice(-5),
    commonClosings: [...new Set([...(existing.commonClosings || []), closing])].slice(-5),
    sampleCount: n + 1,
    lastUpdated: new Date().toISOString(),
  };

  saveLearnedPatterns(userId, updated);
}

// ── System prompt builder ─────────────────────────────────────────────────────
export function buildStyleSystemPrompt(profile, patterns = null) {
  const p = profile || DEFAULT_PROFILE;

  const toneStr = p.toneKeywords?.length ? p.toneKeywords.join(', ') : 'professional';

  const avoidStr = p.wordsToAvoid?.length
    ? p.wordsToAvoid.join(', ')
    : 'none specified';

  const greetingOption = GREETING_OPTIONS.find(g => g.value === p.greetingStyle);
  const greetingStr = greetingOption ? greetingOption.label : 'Hi [name]';

  const sentenceOption = SENTENCE_STYLE_OPTIONS.find(s => s.value === p.sentenceStyle);
  const sentenceStr = sentenceOption ? sentenceOption.label.toLowerCase() : 'balanced';

  let learnedStr = '';
  if (patterns?.sampleCount > 0) {
    const complexity = patterns.vocabularyComplexity <= 4
      ? 'simple and accessible'
      : patterns.vocabularyComplexity <= 6
        ? 'moderate'
        : 'sophisticated';
    learnedStr = `\nLearned from the user's own writing (${patterns.sampleCount} sample${patterns.sampleCount !== 1 ? 's' : ''}):\n- Average sentence length: ~${patterns.avgSentenceLength} words\n- Vocabulary style: ${complexity}`;
    if (patterns.commonOpenings?.length) {
      learnedStr += `\n- Typical opening: "${patterns.commonOpenings[patterns.commonOpenings.length - 1]}"`;
    }
  }

  return `You are Aidey, a professional executive assistant helping the user with their own writing.

The user's writing style profile:
Tone: ${toneStr}
Avoid these words/phrases: ${avoidStr}
Preferred greeting: ${greetingStr}
Preferred sign-off: ${p.signOff || 'not specified'}
Sentence style: ${sentenceStr}${learnedStr}

Help the user with their writing in a way that matches this style. Do not write full documents for them, suggest improvements, offer alternative phrases, or help them get unstuck. Always preserve the user's own voice.`;
}
